import {
    createTheme,
    defaultCssVariablesResolver,
    type CSSVariablesResolver,
    type MantineColorsTuple
} from '@mantine/core'
import classes from './theme.module.css'
import formClasses from './theme-form.module.css'
import modalClasses from './theme-modal.module.css'
import { APP_MODAL_OVERLAY_PROPS, APP_MODAL_SIZE, APP_MODAL_TRANSITION_PROPS } from './modal-config'

/**
 * Eltrust — tokens aligned with `branding.html` (Farben + Google Fonts).
 * Missing `css/ui.css` in-repo: hex values come from the branding page swatches and chart markup.
 */
export const eltrustTokens = {
    font: {
        display: 'var(--font-fredoka, system-ui), system-ui, sans-serif',
        body: 'var(--font-inter, ui-sans-serif), ui-sans-serif, system-ui, -apple-system, sans-serif',
        ui: 'var(--font-plus-jakarta, ui-sans-serif), ui-sans-serif, system-ui, -apple-system, sans-serif'
    },
    color: {
        primary: '#C96F5A',
        primaryDark: '#9F4F43',
        primarySoft: '#FBE2D9',
        secondary: '#F2B59C',
        sage: '#A8BFA3',
        sageSoft: '#EEF5EC',
        surface: '#FFF8F3',
        surfaceCard: '#FFFDF9',
        surfaceSunken: '#F5EDE7',
        surfaceRaised: '#FFFFFF',
        ink: '#213033',
        inkMuted: '#73645F',
        inkSubtle: '#958A85',
        rule: '#EADDD5',
        ruleStrong: '#C9B8AD',
        success: '#7FA889',
        caution: '#DFA64A',
        danger: '#C8574D',
        focus: '#C96F5A',
        focusOutline: '#6b7a7d',
        onPrimary: '#FFFFFF'
    },
    text: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        md: '1.125rem',
        lg: '1.375rem',
        xl: '1.75rem',
        '2xl': '2.25rem',
        '3xl': '3rem'
    },
    leading: {
        tight: '1.15',
        snug: '1.3',
        normal: '1.5',
        relaxed: '1.65'
    },
    radius: {
        sm: '18px',
        md: '12px',
        lg: '16px',
        xl: '20px',
        /** Capsule / pill controls — matches branding `.btn` */
        pill: '9999px'
    },
    /** Marketing hero (split landing) — ink RGB matches `color.ink` #213033 */
    shadow: {
        heroButton: '0 12px 28px rgba(33, 48, 51, 0.12), 0 4px 10px rgba(33, 48, 51, 0.06)',
        heroImageInset: 'inset 18px 0 42px -12px rgba(33, 48, 51, 0.18)',
        /** Desktop: depth between hero copy panel and full-bleed photo */
        heroImageEdge:
            '-40px 0 80px rgba(33, 48, 51, 0.22), -22px 0 44px rgba(33, 48, 51, 0.16), -10px 0 22px rgba(33, 48, 51, 0.12)',
        /** Stacked layout: panel lifts slightly over the image band */
        heroPanelLift: '0 14px 32px rgba(33, 48, 51, 0.08)',
        /** Card/Paper surfaces — no border; definition from light + shadow */
        surfaceRaised: '0 8px 28px rgba(33, 48, 51, 0.08), 0 2px 8px rgba(33, 48, 51, 0.04)'
    },
    /** Shell: burger, notifications, user avatar, PageHeader back — shared hit target (px). */
    layout: {
        headerChromePx: 76,
        headerChromeGlyphPx: 28
    }
} as const

const warmGray = [
    '#FFFDF9',
    '#FFF8F3',
    '#F4EBE3',
    '#EADDD5',
    '#DFCBC1',
    '#C9B5AA',
    '#A3948E',
    '#73645F',
    '#3F3631',
    '#213033'
] as const satisfies MantineColorsTuple

const brandPrimary = [
    '#FFF8F6',
    '#FBE2D9',
    '#F2C4B2',
    '#E8A892',
    '#D8826E',
    '#C96F5A',
    '#B35F4B',
    '#9F4F43',
    '#834338',
    '#6A382E'
] as const satisfies MantineColorsTuple

const brandSage = [
    '#F7FAF7',
    '#EEF5EC',
    '#DCE8D8',
    '#C5D4BF',
    '#B3C7AC',
    '#A8BFA3',
    '#92AB8C',
    '#7A9374',
    '#637B5E',
    '#4E624A'
] as const satisfies MantineColorsTuple

const brandPeach = [
    '#FFF9F6',
    '#FFF0E8',
    '#FCE0D2',
    '#F7C9B3',
    '#F2B59C',
    '#E8A184',
    '#D8896A',
    '#C07058',
    '#9E5A47',
    '#7F4838'
] as const satisfies MantineColorsTuple

const brandSuccess = [
    '#F3F8F4',
    '#E3EEE5',
    '#CDDFD1',
    '#B5CEBA',
    '#9FBDA6',
    '#7FA889',
    '#6B9074',
    '#5A795F',
    '#4A614D',
    '#3B4F3F'
] as const satisfies MantineColorsTuple

const brandWarning = [
    '#FFF9F2',
    '#FFEFDD',
    '#F8DFB8',
    '#F0CB8A',
    '#E8B763',
    '#DFA64A',
    '#C48E38',
    '#A6742C',
    '#855C23',
    '#6A4820'
] as const satisfies MantineColorsTuple

const brandDanger = [
    '#FFF5F4',
    '#FFE8E5',
    '#F8D4CF',
    '#F0B5AD',
    '#E68F83',
    '#C8574D',
    '#AE4A41',
    '#8F3D36',
    '#72332E',
    '#5C2A26'
] as const satisfies MantineColorsTuple

export const theme = createTheme({
    autoContrast: true,
    black: eltrustTokens.color.ink,
    white: eltrustTokens.color.surfaceRaised,
    primaryColor: 'primary',
    primaryShade: { light: 5, dark: 5 },
    focusRing: 'never',
    respectReducedMotion: true,
    cursorType: 'pointer',
    fontFamily: eltrustTokens.font.body,
    fontFamilyMonospace:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    headings: {
        fontFamily: eltrustTokens.font.display,
        fontWeight: '600',
        textWrap: 'balance',
        sizes: {
            h1: { fontSize: eltrustTokens.text['2xl'], lineHeight: eltrustTokens.leading.tight },
            h2: { fontSize: eltrustTokens.text.xl, lineHeight: eltrustTokens.leading.tight },
            h3: { fontSize: eltrustTokens.text.lg, lineHeight: eltrustTokens.leading.snug },
            h4: { fontSize: eltrustTokens.text.md, lineHeight: eltrustTokens.leading.snug },
            h5: { fontSize: eltrustTokens.text.base, lineHeight: eltrustTokens.leading.snug },
            h6: { fontSize: eltrustTokens.text.sm, lineHeight: eltrustTokens.leading.snug }
        }
    },
    fontSizes: {
        xs: eltrustTokens.text.xs,
        sm: eltrustTokens.text.sm,
        md: eltrustTokens.text.base,
        lg: eltrustTokens.text.md,
        xl: eltrustTokens.text.lg
    },
    lineHeights: {
        xs: eltrustTokens.leading.tight,
        sm: eltrustTokens.leading.snug,
        md: eltrustTokens.leading.normal,
        lg: eltrustTokens.leading.relaxed,
        xl: eltrustTokens.leading.relaxed
    },
    radius: {
        xs: eltrustTokens.radius.sm,
        sm: eltrustTokens.radius.sm,
        md: eltrustTokens.radius.md,
        lg: eltrustTokens.radius.lg,
        xl: eltrustTokens.radius.lg,
        pill: eltrustTokens.radius.pill
    },
    defaultRadius: '24px',
    colors: {
        primary: brandPrimary,
        sage: brandSage,
        peach: brandPeach,
        gray: warmGray,
        red: brandDanger,
        green: brandSuccess,
        yellow: brandWarning
    },
    components: {
        Anchor: {
            defaultProps: {
                underline: 'never'
            }
        },
        Avatar: {
            defaultProps: {
                radius: '50%'
            }
        },
        Button: {
            defaultProps: {
                radius: 'pill'
            },
            classNames: classes
        },
        Card: {
            defaultProps: {
                bg: 'white',
                c: eltrustTokens.color.ink,
                withBorder: false,
                shadow: eltrustTokens.shadow.surfaceRaised
            }
        },
        Paper: {
            defaultProps: {
                bg: 'white',
                c: eltrustTokens.color.ink,
                withBorder: false,
                radius: '24px',
                shadow: eltrustTokens.shadow.surfaceRaised
            }
        },
        Input: {
            defaultProps: {
                radius: 'md'
            },
            classNames: {
                input: formClasses.input
            }
        },
        TextInput: {
            defaultProps: {
                radius: 'md'
            },
            classNames: {
                label: formClasses.label,
                description: formClasses.description,
                input: formClasses.input
            }
        },
        DatePickerInput: {
            defaultProps: {
                radius: 'md'
            },
            classNames: {
                label: formClasses.label,
                description: formClasses.description,
                input: formClasses.input
            }
        },
        Select: {
            defaultProps: {
                radius: 'md'
            },
            classNames: {
                label: formClasses.label,
                description: formClasses.description,
                input: formClasses.input
            }
        },
        Modal: {
            defaultProps: {
                centered: true,
                radius: 'xl',
                size: APP_MODAL_SIZE,
                padding: 'xl',
                transitionProps: APP_MODAL_TRANSITION_PROPS,
                overlayProps: APP_MODAL_OVERLAY_PROPS
            },
            classNames: modalClasses
        }
    },
    other: {
        eltrustTokens
    }
})

export const cssVariablesResolver: CSSVariablesResolver = (theme) => {
    const defaultVariables = defaultCssVariablesResolver(theme)

    return {
        variables: {
            ...defaultVariables.variables,
            '--eltrust-font-display': eltrustTokens.font.display,
            '--eltrust-font-body': eltrustTokens.font.body,
            '--eltrust-font-ui': eltrustTokens.font.ui,
            '--eltrust-color-surface': eltrustTokens.color.surface,
            '--eltrust-color-surface-card': eltrustTokens.color.surfaceCard,
            '--eltrust-color-surface-sunken': eltrustTokens.color.surfaceSunken,
            '--eltrust-color-surface-raised': eltrustTokens.color.surfaceRaised,
            '--eltrust-color-ink': eltrustTokens.color.ink,
            '--eltrust-color-ink-muted': eltrustTokens.color.inkMuted,
            '--eltrust-color-ink-subtle': eltrustTokens.color.inkSubtle,
            '--eltrust-color-rule': eltrustTokens.color.rule,
            '--eltrust-color-rule-strong': eltrustTokens.color.ruleStrong,
            '--eltrust-color-primary': eltrustTokens.color.primary,
            '--eltrust-color-primary-dark': eltrustTokens.color.primaryDark,
            '--eltrust-color-primary-soft': eltrustTokens.color.primarySoft,
            '--eltrust-color-secondary': eltrustTokens.color.secondary,
            '--eltrust-color-sage': eltrustTokens.color.sage,
            '--eltrust-color-sage-soft': eltrustTokens.color.sageSoft,
            /** Back-compat + Mantine primary — warm terracotta “Primärfarbe” */
            '--eltrust-color-accent': eltrustTokens.color.primary,
            '--eltrust-color-accent-hover': eltrustTokens.color.primaryDark,
            '--eltrust-color-accent-ink': eltrustTokens.color.onPrimary,
            '--eltrust-color-accent-subtle': eltrustTokens.color.primarySoft,
            '--eltrust-color-caution': eltrustTokens.color.caution,
            '--eltrust-color-success': eltrustTokens.color.success,
            '--eltrust-color-danger': eltrustTokens.color.danger,
            '--eltrust-color-focus': eltrustTokens.color.focus,
            '--eltrust-color-focus-outline': eltrustTokens.color.focusOutline,
            '--eltrust-text-xs': eltrustTokens.text.xs,
            '--eltrust-text-sm': eltrustTokens.text.sm,
            '--eltrust-text-base': eltrustTokens.text.base,
            '--eltrust-text-md': eltrustTokens.text.md,
            '--eltrust-text-lg': eltrustTokens.text.lg,
            '--eltrust-text-xl': eltrustTokens.text.xl,
            '--eltrust-text-2xl': eltrustTokens.text['2xl'],
            '--eltrust-text-3xl': eltrustTokens.text['3xl'],
            '--eltrust-leading-tight': eltrustTokens.leading.tight,
            '--eltrust-leading-snug': eltrustTokens.leading.snug,
            '--eltrust-leading-normal': eltrustTokens.leading.normal,
            '--eltrust-leading-relaxed': eltrustTokens.leading.relaxed,
            '--eltrust-radius-sm': eltrustTokens.radius.sm,
            '--eltrust-radius-md': eltrustTokens.radius.md,
            '--eltrust-radius-lg': eltrustTokens.radius.lg,
            '--eltrust-radius-pill': eltrustTokens.radius.pill,
            '--eltrust-shadow-hero-button': eltrustTokens.shadow.heroButton,
            '--eltrust-shadow-hero-image-inset': eltrustTokens.shadow.heroImageInset,
            '--eltrust-shadow-hero-image-edge': eltrustTokens.shadow.heroImageEdge,
            '--eltrust-shadow-hero-panel-lift': eltrustTokens.shadow.heroPanelLift,
            '--eltrust-shadow-surface-raised': eltrustTokens.shadow.surfaceRaised,
            '--eltrust-layout-header-chrome': `${eltrustTokens.layout.headerChromePx}px`,
            '--app-modal-padding': 'clamp(1.5rem, 4vw, 2.25rem)',
            '--app-modal-width': APP_MODAL_SIZE
        },
        light: {
            ...defaultVariables.light,
            '--mantine-color-body': eltrustTokens.color.surface,
            '--mantine-color-text': eltrustTokens.color.ink,
            '--mantine-color-anchor': eltrustTokens.color.primary,
            '--mantine-color-error': eltrustTokens.color.danger,
            '--mantine-primary-color-filled': eltrustTokens.color.primary,
            '--mantine-primary-color-filled-hover': eltrustTokens.color.primaryDark,
            '--mantine-primary-color-light': eltrustTokens.color.primarySoft,
            '--mantine-primary-color-light-hover': '#F6DED4',
            '--mantine-primary-color-light-color': eltrustTokens.color.primaryDark,
            '--mantine-primary-color-contrast': eltrustTokens.color.onPrimary,
            '--mantine-color-default-border': eltrustTokens.color.rule,
            '--mantine-color-default-hover': eltrustTokens.color.surfaceSunken
        },
        dark: defaultVariables.dark
    }
}
